import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "../../util/axios";
import Token from "../../util/Token";
import { getTeamUrl, priceGroupHistoryUrl } from "src/react-query/endPoints";

const fmtWhen = (v) => (v ? new Date(v).toLocaleString() : "-");

const ACTION_COLOR = {
  CREATE: "success",
  UPDATE: "info",
  DELETE: "error",
};

// Human labels for the tracked fields.
const FIELD_LABELS = {
  name: "Name",
  description: "Description",
  currency: "Currency",
  taxPercent: "Tax %",
  transactionPercent: "Transaction %",
  pgChargesBearer: "PG charges bearer",
  priceGroupType: "Type",
  paymentGatewayId: "Payment gateway",
  priceDetails: "Pricing schedule",
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
};

/**
 * Change history for a price group. Visible to anyone who can view the price
 * group. Shows who changed what and when; the earliest entry is the initial
 * recorded state (no diff).
 */
const PriceGroupHistory = ({ teamId, priceGroupId }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!teamId || !priceGroupId) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        Token.setAuthToken();
        const { data } = await axios.get(
          getTeamUrl(teamId, priceGroupHistoryUrl(priceGroupId))
        );
        if (active) setRows(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        if (active)
          setError(
            e?.response?.data?.message || "Failed to load change history"
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [teamId, priceGroupId]);

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ boxShadow: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Change history
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Who changed this price group, when, and what changed.
        </Typography>

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No changes recorded yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Changes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {fmtWhen(r.createdAt)}
                    </TableCell>
                    <TableCell>
                      {r.changedByEmail ||
                        (r.changedBy ? `User #${r.changedBy}` : "—")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.action}
                        color={ACTION_COLOR[r.action] || "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {r.action === "CREATE" ? (
                        <Typography variant="body2" color="text.secondary">
                          Price group created
                        </Typography>
                      ) : r.action === "DELETE" ? (
                        <Typography variant="body2" color="text.secondary">
                          Price group deleted
                        </Typography>
                      ) : Array.isArray(r.changes) && r.changes.length > 0 ? (
                        <Stack spacing={0.5}>
                          {r.changes.map((c, i) => (
                            <Typography key={i} variant="body2">
                              <strong>{FIELD_LABELS[c.field] || c.field}:</strong>{" "}
                              {fmtVal(c.old)} → {fmtVal(c.new)}
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Updated
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default PriceGroupHistory;
